import React, { useEffect, useMemo, useState } from 'react';
import DataTable, { DATA_TABLE_HIGHLIGHTED_ROW_CLASS } from '../components/DataTable';
import PageTitle from "../components/PageTitle";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ProviderEnum from "../enum/Provider.enum";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Tooltip
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { RootState } from "../store";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { GridRenderCellParams } from "@mui/x-data-grid/models/params/gridCellParams";
import { GridRowClassNameParams } from "@mui/x-data-grid/models/params/gridRowParams";
import apiClient from "../utils/apiClient";
import StyledDrawer from "../components/StyledDrawer";
import AgentLeadsExtra from "../components/AgentLeadsExtra";
import { isAccountOlderThanThreshold } from "../utils/leadsUtils";

const MyLeads = () => {
    const { t } = useTranslation();

    const { isAdmin } = useSelector((state: RootState) => state.user);
    const provider = useSelector((state: RootState) => state.app.provider);

    const [utmSource, setUtmSource] = useState<string>();
    const [utmSources, setUtmSources] = useState<string[]>();
    const [utmSourcesLoading, setUtmSourcesLoading] = useState<boolean>(false);
    const [selectedAgent, setSelectedAgent] = useState<{ id: number; fio?: string; user_id_tn?: string } | null>(null);

    const pageTitle = isAdmin ? t("myLeads.titleAdmin") : t("myLeads.titleAgent");

    const agentColumns = useMemo<GridColDef[]>(() => [
        { field: "id", headerName: t("common.id"), flex: 0.5 },
        {
            field: "fio",
            headerName: t("myLeads.agentFio"),
            flex: 2,
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    sx={{ cursor: "pointer", color: "primary.main", width: "100%" }}
                    onClick={() => setSelectedAgent(params.row)}
                >
                    {params.value}
                </Box>
            ),
        },
        { field: "email", headerName: t("common.email"), flex: 1 },
        {
            field: "action",
            headerName: "",
            width: 140,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAgent(params.row);
                    }}
                >
                    {t("myLeads.leads")}
                </Button>
            ),
        },
    ], [t]);

    const columns = useMemo<GridColDef<any>[]>(
        () => {
            const result: GridColDef[] = [
                { field: 'account_number', headerName: t("myLeads.accountNumber"), flex: 1 },
                { field: 'client_initials', headerName: t("myLeads.clientFio"), flex: 1 },
                { field: 'manager_initials', headerName: t("myLeads.managerFio"), flex: 1 },
                { field: 'status', headerName: t("myLeads.status"), flex: 1 },
                {
                    field: 'date_open_real',
                    headerName: t("myLeads.openDate"),
                    flex: 1,
                    valueFormatter: (value: string) => value ? dayjs(value).format('DD.MM.YYYY') : '',
                    renderCell: (params: GridRenderCellParams) => {
                        const formatted = params.value ? dayjs(params.value).format('DD.MM.YYYY') : '';

                        // Подсветка "старых" счетов — правило актуально только для Беларуси
                        // (там же, где уже исключаются счета старше 2 лет при расчётах).
                        if (provider !== ProviderEnum.BELARUS || !isAccountOlderThanThreshold(params.value)) {
                            return formatted;
                        }

                        return (
                            <Tooltip title={t("myLeads.oldAccountTooltip")} followCursor>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        height: "100%",
                                    }}
                                >
                                    <span>{formatted}</span>
                                    <WarningAmberIcon
                                        color="warning"
                                        fontSize="small"
                                        sx={{ display: "block" }}
                                    />
                                </Box>
                            </Tooltip>
                        );
                    },
                },
                { field: 'utm_source', headerName: t("myLeads.source"), flex: 1 },
            ];

            if (provider === ProviderEnum.BELARUS) {
                result.splice(2, 0, {
                    field: 'citizenship_code',
                    headerName: '',
                    width: 50,
                    sortable: false,
                    filterable: false,
                    renderCell: (params: GridRenderCellParams) => {
                        switch (params.value) {
                            case 'BY':
                                return (
                                    <Tooltip title={t("myLeads.belarusResident")} followCursor>
                                        <img
                                            src="/images/flags/by.png"
                                            width={30}
                                            height={15}
                                            alt="BY"
                                            style={{ borderRadius: 2, verticalAlign: 'middle' }}
                                        />
                                    </Tooltip>
                                );
                            default:
                                return null;
                        }
                    },
                });
            }

            return result;
        },
        [provider, t]
    );

    const getRowClassName = (params: GridRowClassNameParams) =>
        provider === ProviderEnum.BELARUS && isAccountOlderThanThreshold(params.row.date_open_real)
            ? DATA_TABLE_HIGHLIGHTED_ROW_CLASS
            : '';

    const getUtmSources = async () => {
        try {
            setUtmSourcesLoading(true);

            const response = await apiClient.get('/agents/leads_utm_sources');

            setUtmSources(response?.data?.items ?? []);
        } catch (error) {
            console.error("Ошибка загрузки данных", error);
        } finally {
            setUtmSourcesLoading(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) {
            getUtmSources();
        }
    }, [isAdmin]);

    return (
        <>
            <Helmet>
                <title>{pageTitle} | {t("common.partnerCabinet")}</title>
                <meta name="description" content={t("common.partnerCabinet")} />
            </Helmet>

            <PageTitle>{pageTitle}</PageTitle>

            {isAdmin ? (
                <DataTable
                    isShowSearch={true}
                    columns={agentColumns}
                    path="/agents"
                    onRowClick={(row) => setSelectedAgent(row)}
                />
            ) : (
                <>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 150, mr: 1 }} size="small" disabled={utmSourcesLoading}>
                            <InputLabel id="leads-utm_source-label">
                                {t("myLeads.source")}
                            </InputLabel>

                            <Select
                                labelId="leads-utm_source-label"
                                id="leads-utm_source"
                                value={utmSource}
                                label={t("myLeads.source")}
                                size="small"
                                onChange={(e) => setUtmSource(e.target.value)}
                                variant="outlined"
                                disabled={utmSourcesLoading}
                                input={
                                    <OutlinedInput
                                        size="small"
                                        label={t("myLeads.source")}
                                        endAdornment={
                                            utmSourcesLoading && (
                                                <InputAdornment position="end" sx={{ mr: 2 }}>
                                                    <CircularProgress size={20} />
                                                </InputAdornment>
                                            )
                                        }
                                    />
                                }
                            >
                                <MenuItem value="">
                                    {t("common.any")}
                                </MenuItem>

                                {utmSources?.map((source) => (
                                    <MenuItem key={source} value={source}>
                                        {source}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <DataTable
                        columns={columns}
                        path="/agents/leads"
                        queryParams={{
                            utm_source: utmSource || null,
                        }}
                        getRowClassName={getRowClassName}
                    />
                </>
            )}

            <StyledDrawer
                show={Boolean(isAdmin && selectedAgent?.id)}
                width="80vw"
                title={t("myLeads.agentTitle", {
                    fio: selectedAgent?.fio,
                    tnId: selectedAgent?.user_id_tn,
                })}
                onClose={() => setSelectedAgent(null)}
                ContentComponent={AgentLeadsExtra}
                contentProps={{ agent: selectedAgent }}
            />
        </>
    );
};

export default MyLeads;
